import { Database, Calculator, Send } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const STEPS = [
  {
    id: "input",
    number: "01",
    icon: Database,
    title: "Input Your Assets",
    desc: "Plug in your wells, working interests, and partner revenue splits into your secure dashboard.",
    tag: "Setup",
  },
  {
    id: "automate",
    number: "02",
    icon: Calculator,
    title: "Automate the Calculations",
    desc: "GushPay automatically tracks joint venture expenses, runs the math, and generates clean, modern JIB reports.",
    tag: "Engine",
  },
  {
    id: "execute",
    number: "03",
    icon: Send,
    title: "Execute Payouts Instantly",
    desc: "Distribute revenues and settle balances safely through secure, automated digital pipelines. No paper checks, no delays.",
    tag: "Payout",
  },
];

function StepCard({ step, index }) {
  const ref = useReveal();
  const Icon = step.icon;
  return (
    <article
      ref={ref}
      data-testid={`how-step-${step.id}`}
      style={{ transitionDelay: `${index * 100}ms` }}
      className="reveal relative bg-white border border-[#D3D9DE] p-8 lg:p-10 flex flex-col h-full transition-all duration-300 hover:-translate-y-1 hover:border-[#D67229] hover:shadow-[12px_12px_0_0_#082933]"
    >
      <div className="flex items-start justify-between mb-10">
        <span className="font-display text-5xl md:text-6xl text-[#0F4C5C]/15 leading-none">
          {step.number}
        </span>
        <div
          aria-hidden
          className="h-12 w-12 border border-[#111418] flex items-center justify-center text-[#0F4C5C]"
        >
          <Icon size={20} strokeWidth={1.6} />
        </div>
      </div>

      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D67229] mb-3">
        // {step.tag}
      </span>
      <h3 className="font-display text-2xl md:text-[1.7rem] leading-tight text-[#111418]">
        {step.title}
      </h3>
      <p className="mt-4 text-[15px] leading-relaxed text-[#2C333A] flex-1">
        {step.desc}
      </p>

      <div className="mt-8 pt-5 border-t border-[#E9ECEF]">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0F4C5C]">
          Step {step.number} of 03
        </span>
      </div>
    </article>
  );
}

export default function HowItWorksSection() {
  const headerRef = useReveal();

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative bg-[#EBE9E1] py-24 md:py-32"
    >
      <div className="gp-container">
        <div
          ref={headerRef}
          className="reveal grid grid-cols-12 gap-y-6 md:gap-x-12 mb-16 md:mb-20"
        >
          <div className="col-span-12 md:col-span-6">
            <span className="gp-overline">// The 3-step simplicity</span>
            <h2
              data-testid="how-heading"
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#111418] leading-[1.05]"
            >
              How GushPay <span className="italic text-[#0F4C5C]">Works.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-lg text-[#2C333A] leading-relaxed">
              From day-one setup to monthly distributions — three steps that
              replace dozens of spreadsheet hours and manual JIB calculations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {STEPS.map((step, i) => (
            <StepCard key={step.id} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
